import { useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/router';

import { Box, Button, Heading, Paragraph, Popover, TextButton, VisuallyHidden } from '@zillow/constellation-10';

import { useIsMutating } from '@zg-rentals/react-query5';
import { FeeType } from '@zg-rentals/ts-rental-manager-api-client';
import { useRentalManagerUserPropertySingleFamilyCostAndFeesWebApiHandler_getFeesForType } from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { AccordionStateProvider } from '../../../../contexts/AccordionStateContext';
import { useTourSchedulingContext } from '../../../../contexts/TourSchedulingContext';
import { useScreenSize } from '../../../../hooks';
import { useMultiplexListingFlow } from '../../../../hooks';
import { useGetListingDetails } from '../../../../hooks/queries/properties';
import { ACTIVATE_LISTING_MUTATION_KEY, useActivateListing } from '../../../../hooks/queries/useActivateListing';
import useObserveVisible from '../../../../hooks/useObserveVisible';
import accessUtils from '../../../../utils/accessUtils';
import { getRequiredFieldsErrors } from '../../../SFAccordion/utils';

import { PRODUCT_TYPE_NAME_TO_ID } from '../../../../constants/FreemiumConstants';
import { getDashboardOverviewRoute } from '../../../../constants/urls';

import ListingPostedForSaleBanner from '../../../../components/PostingPath/ListingPostedForSaleBanner';
import FreemiumErrorModal from '../../../modals/FreemiumErrorModal';
import ListingPreviewModal from '../../../modals/ListingPreviewModal';
import PreActivationModal from '../../../modals/PreActivationModal';
import SaveAndExitModal from '../../../modals/SaveAndExitModal';
import SFTotalMonthlyPriceWithFeeAcknowledgementModal from '../../../modals/SFTotalMonthlyPriceWithFeeAcknowledgementModal';
import TransferFeedListingModal from '../../../modals/TransferFeedListingModal';
import ContentSectionEditButton from './components/ContentSectionEditButton';
import OwnershipVerificationWarning from './components/OwnershipVerificationWarning';
import PropertyCard from './components/PropertyCard';
import ValidationErrorGuide from './components/ValidationErrorGuide';

import { emitActivateListingEvent } from '../../../../analytics/events/payAndPublish';
import { emitPreviewListingEvent, EVENT_IDS } from '../../../../analytics/events/reviewPage';
import { useAuth } from '../../../AuthProvider';
import ListingActivationTerms from '../../../ListingActivationTerms';
import Banners from '../../../ListingDetails/Banners';
import DuplicateListingDetails from '../../../ListingDetails/DuplicateListingDetails';
import Footer from '../../../ListingDetails/Footer';
import ListingHeader from '../../../ListingDetails/ListingHeader';
import ListingStats from '../../../ListingDetails/ListingStats';
import SFAccordion from '../../../SFAccordion';
import PageWizard from '../../../Wizard/PageWizard';
import { useWizardNavigation } from '../../WizardNavigationProvider';

import type { FreemiumDatarouterError } from '../../../modals/FreemiumErrorModal';
import type { ErrorGuideState } from './components/ValidationErrorGuide/useValidationErrorGuide';
import type { WizardBodyTemplateProps } from '../../../WizardStepController';

const BUTTON_FIELD_NAME = 'preview-listing-button';
const PREVIEW_LISTING_BUTTON_TEXT = 'Preview listing';
const PREVIEW_BUTTON_DISABLED_MESSAGE = 'All the required fields must be completed';
const noOp = () => {};

const Review = ({
  onGoToNextStep,
  onGoToPrevStep,
  onExitWizard,
  wizardTitle,
  progressStepper,
  totalSteps,
  currentStepIndex,
  showDesktopViewBreakpoint,
  ...rest
}: WizardBodyTemplateProps): JSX.Element => {
  const router = useRouter();
  const { user, isAuthorized } = useAuth();
  const { isScreenMobile } = useScreenSize();

  const listingAlias = router.query.listingAlias as string;

  const { data: listingDetails } = useGetListingDetails(listingAlias);

  const active = listingDetails?.active ?? false;
  const isFeed = listingDetails?.isFeed ?? false;
  const isUpdateActiveStatusAllowed = listingDetails?.updateActiveStatus?.isAllowed ?? false;
  const isFreemiumEnabled = listingDetails?.freemium?.isEnabled ?? false;

  const showListingPostedForSaleBanner = !active && !listingDetails?.isFeed && listingDetails?.forSaleExits;
  const isActiveProductTypePremium = listingDetails?.freemium?.activeProduct?.type === PRODUCT_TYPE_NAME_TO_ID.PREMIUM;
  const showPremiumUpsell = isFreemiumEnabled && !isActiveProductTypePremium;
  const isNotActiveAndUpdateActiveStatusNotAllowed = !active && !isUpdateActiveStatusAllowed;

  const showDupeDetails = !!listingDetails?.topDupesDetails?.dupes?.length;

  const canAccessMLReactivationFlow = accessUtils.canAccessMLReactivationFlow({
    lastActivatedMs: listingDetails?.lastActivatedMs,
    isFeed: listingDetails?.isFeed,
    isMultifamily: listingDetails?.isMultifamily,
  });

  const canAccessRelistingFlow = accessUtils.canAccessRelistingFlow({
    canBeRelisted: Boolean(listingDetails?.canBeRelisted),
  });

  const canAccessListingPreview = accessUtils.canAccessListingPreview({
    active,
    isFeed,
    isMultifamily: listingDetails?.isMultifamily ?? false,
    listingType: listingDetails?.listingType,
  });

  const canAccessTotalMonthlyPriceWithFee = accessUtils.canAccessTotalMonthlyPriceWithFee({
    isEligible: listingDetails?.totalMonthlyPriceWithFeeNotice?.isEligible,
  });

  const { isInMultiplexListingFlow } = useMultiplexListingFlow();

  const canFeedConnectSwitchSource = user?.features?.canSwitchListingSource ?? false;

  // Calculate validation errors and canPublish
  const canAccessSFCostsAndFees = accessUtils.canAccessSFCostsAndFees({
    isFeed: listingDetails?.isFeed ?? false,
    isMultifamily: listingDetails?.isMultifamily ?? false,
  });

  const canAccessScreeningCriteria = listingDetails?.listingCriteria?.isEnabled ?? false;

  const canAccessPostingPathTourScheduling = accessUtils.canAccessPostingPathTourScheduling({
    isFeed: listingDetails?.isFeed ?? false,
    isInReactivationFlow: canAccessMLReactivationFlow,
    canAccessRelisting: canAccessRelistingFlow,
    active: listingDetails?.active ?? false,
  });

  const { mutate: activateListing } = useActivateListing();

  const { data: applicationFee = [] } = useRentalManagerUserPropertySingleFamilyCostAndFeesWebApiHandler_getFeesForType(
    { feeType: FeeType.APPLICATION_FEE, listingAlias },
    { enabled: canAccessSFCostsAndFees && Boolean(listingAlias), meta: { tags: ['sf-applicationFee'] } },
  );
  const feeAmount = applicationFee?.length ? applicationFee[0]?.feeAmountMin : null;

  const { tourCalendarApiHook } = useTourSchedulingContext();
  const { availabilityEvents, isTouringSchedulingEnabled } = tourCalendarApiHook;

  const requiredFieldsErrors = useMemo(() => {
    if (!listingDetails || listingDetails.isMultifamily) return {};

    const hasTouringSectionErrors =
      canAccessPostingPathTourScheduling && isTouringSchedulingEnabled && !availabilityEvents?.length;

    return getRequiredFieldsErrors({
      listingDetails,
      feeAmount,
      hasTouringSectionErrors,
      canAccessScreeningCriteriaInListingFlow: canAccessScreeningCriteria,
      isApplicationsUpdateRestricted: listingDetails?.restrictions?.applications?.isUpdateRestricted,
    });
  }, [
    listingDetails,
    feeAmount,
    canAccessPostingPathTourScheduling,
    isTouringSchedulingEnabled,
    availabilityEvents,
    canAccessScreeningCriteria,
  ]);

  const canPublish = useMemo(() => {
    // All values are booleans (true = has error), so canPublish when no errors
    return Object.values(requiredFieldsErrors).every((hasError) => !hasError);
  }, [requiredFieldsErrors]);

  const [errorGuideState, setErrorGuideState] = useState<ErrorGuideState>(null);
  const [showSaveAndExitModal, setShowSaveAndExitModal] = useState(false);
  const [displayListingPreviewModal, setDisplayListingPreviewModal] = useState(false);
  const [
    displaySFTotalMonthlyPriceWithFeeAcknowledgementModal,
    setDisplaySFTotalMonthlyPriceWithFeeAcknowledgementModal,
  ] = useState(false);
  const [showPreActivationModal, setShowPreActivationModal] = useState(false);
  const [publishError, setPublishError] = useState<{
    error?: FreemiumDatarouterError;
    onClose: () => void;
  }>({ onClose: () => {} });

  const isInReactivationFlow = Boolean(isInMultiplexListingFlow) !== true && canAccessMLReactivationFlow;

  const nextLabel = showPremiumUpsell ? 'Next' : 'Publish';
  const isNextDisabled = !canPublish || isNotActiveAndUpdateActiveStatusNotAllowed;

  const { reportError, clearError } = useWizardNavigation();
  useEffect(() => {
    if (isNextDisabled) {
      reportError();
      return () => clearError();
    }
  }, [isNextDisabled, reportError, clearError]);

  const ctaRef = useRef<HTMLButtonElement>(null);

  const [isVisible, isVisibleRef] = useObserveVisible({
    bypassBoundingClientRect: true,
  });

  const isActivateListingPending = useIsMutating({ mutationKey: ACTIVATE_LISTING_MUTATION_KEY }) > 0;

  useEffect(() => {
    if (displayListingPreviewModal && listingDetails) {
      emitPreviewListingEvent({ currentProperty: listingDetails, user });
    }
  }, [displayListingPreviewModal]);

  const previewListingCTA = useMemo(() => {
    return canAccessListingPreview && isUpdateActiveStatusAllowed ? (
      <Box
        css={{ display: 'flex', flex: '1 0 auto', justifyContent: 'flex-end', alignItems: 'center', maxWidth: '152px' }}
      >
        {isNextDisabled ? (
          <Popover
            header={<Heading level={2}>Listing preview is disabled</Heading>}
            body={<Paragraph>{PREVIEW_BUTTON_DISABLED_MESSAGE}</Paragraph>}
            tone="neutral"
            trigger={
              <Button
                tone="neutral"
                emphasis="outlined"
                fluid={true}
                aria-label={`disabled-edit-button-for-${BUTTON_FIELD_NAME}`}
              >
                {PREVIEW_LISTING_BUTTON_TEXT}
              </Button>
            }
          />
        ) : (
          <Button
            data-testid={BUTTON_FIELD_NAME}
            onClick={() => setDisplayListingPreviewModal(true)}
            tone="neutral"
            emphasis="outlined"
            fluid={true}
            data-eventid={EVENT_IDS.PREVIEW_LISTING}
          >
            {PREVIEW_LISTING_BUTTON_TEXT}
          </Button>
        )}
      </Box>
    ) : undefined;
  }, [canAccessListingPreview, isUpdateActiveStatusAllowed, isNextDisabled]);

  const shouldAutoActivateRef = useRef(false);

  const handleNextClick = ({ shouldAutoActivate = false, shouldTriggerDeactivate = false } = {}) => {
    if (ctaRef.current && shouldTriggerDeactivate) {
      ctaRef.current?.click();
      return;
    }

    if (listingDetails?.singleFamilyContractDto.isRequired || isFeed) {
      setShowPreActivationModal(true);
      return;
    }

    if (
      listingDetails?.rentGuarantee?.isEligible &&
      canAccessMLReactivationFlow &&
      !listingDetails?.rentGuarantee?.isEnabled
    ) {
      onGoToNextStep?.();
      return;
    }

    if (canAccessTotalMonthlyPriceWithFee && !listingDetails?.displayTotalMonthlyPriceWithFee) {
      shouldAutoActivateRef.current = shouldAutoActivate;
      setDisplaySFTotalMonthlyPriceWithFeeAcknowledgementModal(true);
      return;
    }

    /**
     * NOTE: since `SFTotalMonthlyPriceWithFeeAcknowledgementModal` is intercepting
     * should continue the logic on `Confirm`
     **/
    if (!isFreemiumEnabled || shouldAutoActivate || isActiveProductTypePremium) {
      autoActivate();
      return;
    }

    if (isFreemiumEnabled) {
      onGoToNextStep?.();
      return;
    }

    onGoToNextStep?.();
  };

  const autoActivate = () => {
    activateListing(
      { postData: { listingAlias } },
      {
        onSuccess: () => {
          if (listingDetails && user) {
            emitActivateListingEvent({ currentProperty: listingDetails, user });
          }

          if (isInMultiplexListingFlow) {
            onGoToNextStep?.();
          } else {
            void router.push(getDashboardOverviewRoute({ listingAlias, showActivationModal: true }));
          }
        },
        onError: (error) => {
          setPublishError({
            error: error as FreemiumDatarouterError,
            onClose: () => {
              setPublishError({ error: undefined, onClose: () => {} });
            },
          });
        },
      },
    );
  };

  const handleExitWithModal = () => {
    if (!isFeed && !listingDetails?.active) {
      setShowSaveAndExitModal(true);
    } else {
      onExitWizard?.();
    }
  };

  return (
    <AccordionStateProvider>
      <PageWizard
        {...rest}
        title=""
        wizardTitle={wizardTitle}
        progressStepper={(!canAccessMLReactivationFlow || canAccessRelistingFlow) && progressStepper}
        totalSteps={totalSteps}
        currentStepIndex={currentStepIndex}
        onNextClick={handleNextClick}
        onBackClick={() => onGoToPrevStep?.()}
        onExitTriggered={handleExitWithModal}
        showDesktopViewBreakpoint={showDesktopViewBreakpoint}
        nextLabel={nextLabel}
        isNextLoading={isActivateListingPending}
        hideStepIndex={isInReactivationFlow}
        hideBackButton={isInReactivationFlow && !canAccessRelistingFlow}
        displayPremiumTopOfFunnelHeader={!isInMultiplexListingFlow}
        footerContent={
          errorGuideState ? (
            <ValidationErrorGuide
              errors={errorGuideState.errors}
              currentErrorIndex={errorGuideState.currentErrorIndex}
              errorMessage={errorGuideState.errorMessage}
              onJumpToError={errorGuideState.onJumpToError}
              onNext={errorGuideState.onNext}
              onPrevious={errorGuideState.onPrevious}
            />
          ) : null
        }
        footer={
          isInReactivationFlow &&
          !canAccessRelistingFlow && (
            <Footer
              ctaRef={ctaRef}
              onPublishClick={handleNextClick}
              isPublishedDisabled={!canPublish}
              isPublishLoading={isActivateListingPending}
            />
          )
        }
        isNextDisabled={isNextDisabled}
      >
        <Box css={{ border: 'none', padding: 0, marginBottom: '75px' }} data-testid="Review">
          <VisuallyHidden>
            <h1>Review page</h1>
          </VisuallyHidden>

          <Box ref={isVisibleRef as React.RefObject<HTMLDivElement>} css={{ marginBottom: '800' }}>
            <PropertyCard previewListingCTA={previewListingCTA} />
          </Box>

          <OwnershipVerificationWarning
            listingAlias={listingAlias}
            enabled={
              accessUtils.canAccessOwnershipVerification() &&
              isAuthorized &&
              !isFeed &&
              !active &&
              Boolean(listingDetails?.forRentBy)
            }
            stateCode={listingDetails?.state}
          />

          {isInReactivationFlow && <Banners />}
          {showListingPostedForSaleBanner && (
            <ListingPostedForSaleBanner forSaleUrlOnZillow={listingDetails?.forSaleUrlOnZillow} />
          )}
          {showDupeDetails && !canFeedConnectSwitchSource && <DuplicateListingDetails />}
          {isFeed && <ListingHeader title={`Source: ${listingDetails?.feedName}`} />}
          {!isFeed && canFeedConnectSwitchSource && <ListingHeader />}
          {user?.isEnterpriseCustomer && <ListingStats />}

          <SFAccordion requiredFieldsErrors={requiredFieldsErrors} onErrorGuideChange={setErrorGuideState} />

          <Box css={{ marginTop: '400' }} />
          <ListingActivationTerms
            isTextCenter={true}
            actionText={`By ${active ? 'deactivating' : 'publishing'} this listing`}
            previewListingCTA={!isScreenMobile && previewListingCTA && !isVisible ? previewListingCTA : undefined}
          />

          {Boolean(isScreenMobile && previewListingCTA && !isVisible) && (
            <Box css={{ display: 'flex', justifyContent: 'center', marginTop: '600' }}>
              {isNextDisabled ? (
                <ContentSectionEditButton
                  isDisabled={isNextDisabled}
                  tone="subtle"
                  buttonFieldName={BUTTON_FIELD_NAME}
                  editButtonText={PREVIEW_LISTING_BUTTON_TEXT}
                  disabledMessage={PREVIEW_BUTTON_DISABLED_MESSAGE}
                  onEditClick={noOp}
                />
              ) : (
                <TextButton
                  data-testid={BUTTON_FIELD_NAME}
                  disabled={isNextDisabled}
                  onClick={() => setDisplayListingPreviewModal(true)}
                >
                  {PREVIEW_LISTING_BUTTON_TEXT}
                </TextButton>
              )}
            </Box>
          )}
        </Box>
        <PreActivationModal
          displayPreActivationModal={showPreActivationModal}
          setDisplayPreActivationModal={setShowPreActivationModal}
          onActivateListing={() => onGoToNextStep?.()}
        />

        {displaySFTotalMonthlyPriceWithFeeAcknowledgementModal && (
          <SFTotalMonthlyPriceWithFeeAcknowledgementModal
            onClose={() => setDisplaySFTotalMonthlyPriceWithFeeAcknowledgementModal(false)}
            onSubmit={() => {
              if (!isFreemiumEnabled || shouldAutoActivateRef.current || isActiveProductTypePremium) {
                shouldAutoActivateRef.current = false;
                autoActivate();
                return;
              }

              if (isFreemiumEnabled) {
                onGoToNextStep?.();
                return;
              }

              onGoToNextStep?.();
            }}
            isSubmitLoading={isActivateListingPending}
          />
        )}

        {displayListingPreviewModal && (
          <ListingPreviewModal onClose={() => setDisplayListingPreviewModal(false)} onSubmit={handleNextClick} />
        )}

        <SaveAndExitModal
          displayModal={showSaveAndExitModal}
          canPublish={canPublish}
          hasNextStep={Boolean(onGoToNextStep)}
          onClose={() => setShowSaveAndExitModal(false)}
          onCancel={onGoToNextStep || noOp}
          onSubmit={() => {
            setShowSaveAndExitModal(false);
            onExitWizard?.();
          }}
        />

        {publishError.error && (
          <FreemiumErrorModal
            error={publishError?.error}
            onClose={publishError?.onClose}
            onPublishForFreeClick={autoActivate}
            onTryAgainClick={autoActivate}
            isPremiumMidfunnelPayment={false}
            selectedProductType={PRODUCT_TYPE_NAME_TO_ID.FREE}
          />
        )}

        <TransferFeedListingModal listingAlias={listingAlias} isMultifamily={listingDetails?.isMultifamily} />
      </PageWizard>
    </AccordionStateProvider>
  );
};

export default Review;
